import { supabase } from "@/platform/lib/supabase";
import { readErrorFromResponse } from "@/platform/billing/errors";

async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error("Sessão expirada. Entre novamente.");
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw await readErrorFromResponse(res);
  return res;
}

export interface AdminOverview {
  users: { total: number; last7d: number };
  subscriptions: { active: number; trialing: number; pastDue: number };
  mrr: { totalBrl: number; einsteinBrl: number; uspBrl: number };
  llm: { last24h: number; errors24h: number; avgLatencyMs: number };
}

export async function fetchAdminOverview(): Promise<AdminOverview> {
  const res = await authedFetch("/api/admin/overview");
  return (await res.json()) as AdminOverview;
}

export interface AdminUserRow {
  user_id: string;
  email: string;
  full_name: string | null;
  display_name: string | null;
  is_admin: boolean;
  email_confirmed_at: string | null;
  banned_until: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  subscriptions: Array<{ tenant: string; plan: string; status: string }>;
}

export interface ListUsersResult {
  users: AdminUserRow[];
  total: number;
}

export async function listAdminUsers(params: {
  search?: string;
  tenant?: "einstein" | "usp" | "all";
  status?: "all" | "active" | "trialing" | "canceled" | "none";
  page?: number;
  pageSize?: number;
}): Promise<ListUsersResult> {
  const q = new URLSearchParams();
  if (params.search) q.set("search", params.search);
  if (params.tenant && params.tenant !== "all") q.set("tenant", params.tenant);
  if (params.status && params.status !== "all") q.set("status", params.status);
  q.set("page", String(params.page ?? 1));
  q.set("pageSize", String(params.pageSize ?? 25));
  const res = await authedFetch(`/api/admin/users?${q.toString()}`);
  return (await res.json()) as ListUsersResult;
}

export interface AdminUserDetail extends Omit<AdminUserRow, "subscriptions"> {
  subscriptions: Array<{
    id: string;
    tenant: string;
    plan: string;
    status: string;
    trial_end: string | null;
    current_period_end: string | null;
    cancel_at: string | null;
    canceled_at: string | null;
    stripe_subscription_id: string | null;
    created_at: string;
  }>;
  einsteinProfile: {
    onboarding_completed: boolean;
    exam_date: string | null;
    weekly_hours: number | null;
    consent_terms_at: string | null;
  } | null;
  uspProfile: {
    onboarding_completed: boolean;
    curso_alvo: string | null;
    weekly_hours: number | null;
  } | null;
  recentLlmCalls: Array<{
    function_name: string;
    model: string;
    status: string;
    latency_ms: number | null;
    created_at: string;
  }>;
}

export async function getAdminUser(userId: string): Promise<AdminUserDetail> {
  const res = await authedFetch(`/api/admin/users/${userId}`);
  return (await res.json()) as AdminUserDetail;
}

export type AdminUserAction =
  | { type: "set_admin"; isAdmin: boolean }
  | { type: "ban"; days: number | null /* null = unban */ }
  | { type: "send_password_reset" }
  | { type: "delete" };

export async function actOnAdminUser(userId: string, action: AdminUserAction): Promise<void> {
  await authedFetch(`/api/admin/users/${userId}/actions`, {
    method: "POST",
    body: JSON.stringify(action),
  });
}

export interface AdminSubRow {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string | null;
  tenant: string;
  plan: string;
  status: string;
  trial_end: string | null;
  current_period_end: string | null;
  cancel_at: string | null;
  canceled_at: string | null;
  stripe_subscription_id: string | null;
  created_at: string;
}

export interface ListSubsResult {
  subscriptions: AdminSubRow[];
  total: number;
}

export async function listAdminSubscriptions(params: {
  search?: string;
  tenant?: "einstein" | "usp" | "all";
  status?: string;
  page?: number;
  pageSize?: number;
}): Promise<ListSubsResult> {
  const q = new URLSearchParams();
  if (params.search) q.set("search", params.search);
  if (params.tenant && params.tenant !== "all") q.set("tenant", params.tenant);
  if (params.status && params.status !== "all") q.set("status", params.status);
  q.set("page", String(params.page ?? 1));
  q.set("pageSize", String(params.pageSize ?? 25));
  const res = await authedFetch(`/api/admin/subscriptions?${q.toString()}`);
  return (await res.json()) as ListSubsResult;
}

export type AdminSubAction = { type: "cancel"; immediate: boolean };

export async function actOnAdminSubscription(
  subId: string,
  action: AdminSubAction,
): Promise<void> {
  await authedFetch(`/api/admin/subscriptions/${subId}/actions`, {
    method: "POST",
    body: JSON.stringify(action),
  });
}

export interface AdminMetrics {
  signupsByDay: Array<{ date: string; count: number }>;
  subsByDay: Array<{ date: string; active: number; trialing: number; canceled: number }>;
  conversionRate: {
    trialsStarted30d: number;
    convertedToActive30d: number;
    rate: number; // 0..1
  };
  churnRate30d: number; // 0..1
  planBreakdown: Array<{ tenant: string; plan: string; count: number; mrrBrl: number }>;
  topUsersByLlm30d: Array<{ user_id: string; email: string; calls: number; cost_estimate: number }>;
}

export async function fetchAdminMetrics(range: 7 | 30 | 90 = 30): Promise<AdminMetrics> {
  const res = await authedFetch(`/api/admin/metrics?range=${range}`);
  return (await res.json()) as AdminMetrics;
}

export interface MrrHistoryPoint {
  date: string;
  einsteinBrl: number;
  uspBrl: number;
  totalBrl: number;
}

export interface MrrHistoryResult {
  series: MrrHistoryPoint[];
  days: number;
}

export async function fetchAdminMrrHistory(
  range: 30 | 90 | 180 = 30,
): Promise<MrrHistoryResult> {
  const res = await authedFetch(`/api/admin/mrr-history?range=${range}`);
  return (await res.json()) as MrrHistoryResult;
}

export interface AdminAuditEntry {
  id: string;
  actor_id: string;
  actor_email: string;
  action: string;
  target_type: "user" | "subscription" | "asset";
  target_id: string;
  target_label: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface ListAuditResult {
  entries: AdminAuditEntry[];
  total: number;
}

/**
 * Sobrescreve a imagem renderizada de uma página da prova USP. Envia o
 * conteúdo como base64 no body JSON. Limite ~5MB (Netlify aceita até 6MB).
 */
export async function replaceUspPageImage(params: {
  paperPageId: string;
  file: File;
}): Promise<{ storage_path: string }> {
  const buffer = await params.file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  const base64 = btoa(binary);
  const mime = (params.file.type || "image/png") as
    | "image/png"
    | "image/jpeg"
    | "image/webp";

  const res = await authedFetch(`/api/admin/replace-page-image`, {
    method: "POST",
    body: JSON.stringify({
      paper_page_id: params.paperPageId,
      content_base64: base64,
      mime_type: ["image/png", "image/jpeg", "image/webp"].includes(mime)
        ? mime
        : "image/png",
    }),
  });
  return (await res.json()) as { storage_path: string };
}

/**
 * Resolve o paper_page_id (e URL atual) de uma página dado exam_paper_id +
 * page_number. Necessário porque o modal de edit conhece só (exam_paper_id,
 * page_number), não o id da linha em usp_paper_pages.
 */
export async function resolveUspPaperPage(
  examPaperId: string,
  pageNumber: number,
): Promise<{ id: string; storage_path: string; publicUrl: string } | null> {
  const { supabase } = await import("@/platform/lib/supabase");
  const { data, error } = await supabase
    .from("usp_paper_pages")
    .select("id, storage_path")
    .eq("exam_paper_id", examPaperId)
    .eq("page_number", pageNumber)
    .maybeSingle();
  if (error || !data) return null;
  const base = import.meta.env.VITE_SUPABASE_URL ?? "";
  return {
    id: data.id as string,
    storage_path: data.storage_path as string,
    publicUrl: `${base}/storage/v1/object/public/usp-question-assets/${data.storage_path}`,
  };
}

export async function fetchAdminAudit(params: {
  actorEmail?: string;
  targetEmail?: string;
  action?: string;
  page?: number;
  pageSize?: number;
}): Promise<ListAuditResult> {
  const q = new URLSearchParams();
  if (params.actorEmail) q.set("actorEmail", params.actorEmail);
  if (params.targetEmail) q.set("targetEmail", params.targetEmail);
  if (params.action) q.set("action", params.action);
  q.set("page", String(params.page ?? 1));
  q.set("pageSize", String(params.pageSize ?? 50));
  const res = await authedFetch(`/api/admin/audit?${q.toString()}`);
  return (await res.json()) as ListAuditResult;
}
